import { useState, useEffect } from "react";
import { useAuth } from "../../hooks/useAuth";
import { portfolioService } from "../../services/portfolioService";
import { transactionService } from "../../services/transactionService";
import meetingService from "../../services/meetingService";
import { advisorService } from "../../services/advisorService";
import { activityLogService } from "../../services/activityLogService";
import type { ActivityLogRequest } from "../../interface/ActivityLogRequest";
import type { ClientPortfolio } from "../../interface/ClientPortfolio";
import type { PortfolioAccountResponse } from "../../interface/PortfolioAccountResponse";
import type { PortfolioPositionResponse } from "../../interface/PortfolioPositionResponse";
import type { CreateTransactionRequest } from "../../interface/CreateTransactionRequest";
import type { AccountStatement } from "../../interface/AccountStatement";
import type { TransactionResponse } from "../../interface/TransactionResponse";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/collapsible";
import {
  ChevronDown,
  ChevronRight,
  Loader2,
  Plus,
  Pencil,
  Trash2,
  X,
  Wallet,
  PiggyBank,
  GraduationCap,
  Heart,
  Briefcase,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Label } from "../ui/label";
import { AddAccount } from "./AddAccount";
import { AddStatement } from "./AddStatement";
import { ScheduleMeetingDialog } from "../ScheduleMeetingDialog";
import { InviteOthers } from "./InviteOthers";

import type { ProfessionalProfile } from "../ScheduleMeetingDialog";
import type { Account, Position } from "../../interface/Account";
import { ACCOUNT_TYPES, OWNERSHIP_TYPES, MEETING_TYPES, ACTIVITY_TYPES_ADVISOR } from "../../utils/financialTaxonomy";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Progress } from "../ui/progress";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  TrendingUp,
  TrendingDown,
  Users,
  DollarSign,
  Target,
  AlertCircle,
  Search,
  Filter,
  Calendar,
  Award,
  MessageSquare,
  Clock,
} from "lucide-react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

interface ClientAccount {
  id: string;
  name: string;
  accountType: "Individual" | "Joint" | "Retirement" | "Trust";
  portfolioValue: number;
  ytdReturn: number;
  monthlyReturn: number;
  riskProfile: "Conservative" | "Moderate" | "Aggressive";
  lastContact: string;
  nextReview: string;
  alerts: number;
  goals: string[];
}

const clientAccounts: ClientAccount[] = [
  {
    id: "1",
    name: "Sarah & John Johnson",
    accountType: "Joint",
    portfolioValue: 375000,
    ytdReturn: 18.5,
    monthlyReturn: 2.3,
    riskProfile: "Moderate",
    lastContact: "2025-01-10",
    nextReview: "2025-03-15",
    alerts: 1,
    goals: ["Retirement", "College Fund"],
  },
  {
    id: "2",
    name: "Michael Chen",
    accountType: "Individual",
    portfolioValue: 1200000,
    ytdReturn: 12.3,
    monthlyReturn: 1.1,
    riskProfile: "Conservative",
    lastContact: "2025-01-08",
    nextReview: "2025-02-20",
    alerts: 0,
    goals: ["Wealth Preservation", "Estate Planning"],
  },
  {
    id: "3",
    name: "Martinez Family Trust",
    accountType: "Trust",
    portfolioValue: 2500000,
    ytdReturn: 15.8,
    monthlyReturn: 1.8,
    riskProfile: "Moderate",
    lastContact: "2024-12-28",
    nextReview: "2025-01-25",
    alerts: 2,
    goals: ["Generational Wealth", "Tax Efficiency"],
  },
  {
    id: "4",
    name: "David Lee IRA",
    accountType: "Retirement",
    portfolioValue: 520000,
    ytdReturn: 10.2,
    monthlyReturn: 0.9,
    riskProfile: "Moderate",
    lastContact: "2025-01-05",
    nextReview: "2025-04-10",
    alerts: 0,
    goals: ["Retirement Income"],
  },
  {
    id: "5",
    name: "Emily Rodriguez",
    accountType: "Individual",
    portfolioValue: 180000,
    ytdReturn: 22.4,
    monthlyReturn: 3.2,
    riskProfile: "Aggressive",
    lastContact: "2025-01-12",
    nextReview: "2025-02-28",
    alerts: 1,
    goals: ["Growth", "Home Purchase"],
  },
];

interface PortfolioAlert {
  clientName: string;
  type: "Rebalancing" | "Review Due" | "Risk Alert" | "Opportunity";
  severity: "High" | "Medium" | "Low";
  message: string;
  date: string;
}

const alerts: PortfolioAlert[] = [
  {
    clientName: "Martinez Family Trust",
    type: "Rebalancing",
    severity: "High",
    message: "Portfolio allocation significantly drifted from target",
    date: "2025-01-14",
  },
  {
    clientName: "Sarah & John Johnson",
    type: "Review Due",
    severity: "Medium",
    message: "Quarterly review scheduled for next week",
    date: "2025-01-13",
  },
  {
    clientName: "Emily Rodriguez",
    type: "Risk Alert",
    severity: "Medium",
    message: "High concentration in technology sector (45%)",
    date: "2025-01-12",
  },
  {
    clientName: "Martinez Family Trust",
    type: "Opportunity",
    severity: "Low",
    message: "Tax-loss harvesting opportunity identified",
    date: "2025-01-11",
  },
];

interface AggregateMetrics {
  category: string;
  count: number;
  totalValue: number;
  avgReturn: number;
}

export function AdvisorPortfolio() {
  const { userId, user } = useAuth();
  const [clientPortfolios, setClientPortfolios] = useState<ClientPortfolio[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>("");
  const [isScheduleMeetingOpen, setIsScheduleMeetingOpen] = useState(false);
  const [meetings, setMeetings] = useState<any[]>([]);
  const [clientGrowthData, setClientGrowthData] = useState<{ month: string; clients: number; aum: number }[]>([]);
  const [recentActivities, setRecentActivities] = useState<any[]>([]);
  const [isLogActivityOpen, setIsLogActivityOpen] = useState(false);
  const [activityForm, setActivityForm] = useState<ActivityLogRequest>({
    userId: userId || "",
    userName: user?.name || "",
    activityType: "",
    description: "",
    recipientUserId: "",
    recipientUserName: "",
    metadata: {},
  });
  const [isClientPortfoliosLoading, setIsClientPortfoliosLoading] = useState(true);
  const [expandedClients, setExpandedClients] = useState<Record<string, boolean>>({});
  const [addingAccountClientId, setAddingAccountClientId] = useState<string | null>(null);
  const [isAddingStatement, setIsAddingStatement] = useState(false);
  const [editingPosition, setEditingPosition] = useState<PortfolioPositionResponse | null>(null);
  const [editingPositionAccountId, setEditingPositionAccountId] = useState<string>("");
  const [deletingPosition, setDeletingPosition] = useState<PortfolioPositionResponse | null>(null);
  const [deletingPositionAccountId, setDeletingPositionAccountId] = useState<string>("");
  const [advisorStatementList, setAdvisorStatementList] = useState<AccountStatement[]>([]);
  const [isAdvisorStatementListLoading, setIsAdvisorStatementListLoading] = useState(false);
  const [advisorStatementListError, setAdvisorStatementListError] = useState<string | null>(null);
  const [clientTransactions, setClientTransactions] = useState<Record<string, TransactionResponse[]>>({});
  const [isTransactionsLoading, setIsTransactionsLoading] = useState(false);
  const [advisorStatementFilters, setAdvisorStatementFilters] = useState(() => {
    const end = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - 6);
    return { startDate: start.toISOString().split("T")[0], endDate: end.toISOString().split("T")[0], periodType: "MONTHLY" };
  });
  const [editingAdvisorStatement, setEditingAdvisorStatement] = useState<AccountStatement | null>(null);
  const [deletingAdvisorStatementId, setDeletingAdvisorStatementId] = useState<string | null>(null);
  const [editingAccount, setEditingAccount] = useState<PortfolioAccountResponse | null>(null);
  const [newPosition, setNewPosition] = useState({ symbol: "", name: "", shares: "", avgCost: "", sector: "" });
  const [deletedPositionIndices, setDeletedPositionIndices] = useState<number[]>([]);
  const [isSavingAccount, setIsSavingAccount] = useState(false);

  const fetchClientAccountStatements = async (filters?: { startDate: string; endDate: string; periodType: string }) => {
    if (!userId) return;
    setIsAdvisorStatementListLoading(true);
    setAdvisorStatementListError(null);
    try {
      const effectiveFilters = filters || advisorStatementFilters;
      const clientIds = clientPortfolios.map((cp) => cp.clientId);
      if (clientIds.length === 0) {
        setAdvisorStatementList([]);
        return;
      }
      const results = await Promise.all(
        clientIds.map((cid) =>
          portfolioService.getAllAccountStatements(cid, {
            startDate: effectiveFilters.startDate,
            endDate: effectiveFilters.endDate,
            periodType: effectiveFilters.periodType,
          }).then((res: any) => (Array.isArray(res) ? res : (res?.data || [])))
        )
      );
      setAdvisorStatementList(results.flat());
    } catch (error) {
      setAdvisorStatementListError(error instanceof Error ? error.message : "Failed to load statements");
    } finally {
      setIsAdvisorStatementListLoading(false);
    }
  };

  const handleDeleteAdvisorStatement = async (statementId: string) => {
    try {
      await portfolioService.deleteAccountStatement(statementId);
      setDeletingAdvisorStatementId(null);
      fetchClientAccountStatements();
    } catch (error) {
      console.error("Failed to delete statement:", error);
    }
  };

  const handleSaveEditedAdvisorStatement = async () => {
    if (!editingAdvisorStatement) return;
    try {
      await portfolioService.updateAccountStatement(editingAdvisorStatement.id, editingAdvisorStatement);
      setEditingAdvisorStatement(null);
      fetchClientAccountStatements();
    } catch (error) {
      console.error("Failed to update statement:", error);
    }
  };

  function buildClientStatementRows(
    statements: AccountStatement[],
    portfolios: ClientPortfolio[],
    expanded: Record<string, boolean>,
    setExpanded: (v: Record<string, boolean>) => void,
    setEditing: (v: AccountStatement | null) => void,
    setDeleting: (v: string | null) => void
  ) {
    const accountToClient = new Map<string, string>();
    portfolios.forEach((cp) => cp.portfolioAccounts.forEach((a) => accountToClient.set(a.id, cp.clientId)));

    const clientGrouped = statements.reduce((acc, stmt) => {
      const cid = accountToClient.get(stmt.accountId) || "unknown";
      if (!acc[cid]) acc[cid] = {};
      if (!acc[cid][stmt.accountId]) acc[cid][stmt.accountId] = [];
      acc[cid][stmt.accountId].push(stmt);
      return acc;
    }, {} as Record<string, Record<string, AccountStatement[]>>);

    const rows: any[] = [];
    for (const [clientId, accountGroups] of Object.entries(clientGrouped)) {
      for (const [accountId, stmts] of Object.entries(accountGroups)) {
        const account = portfolios.flatMap((cp) => cp.portfolioAccounts).find((a) => a.id === accountId);
        const clientPortfolio = portfolios.find((cp) => cp.clientId === clientId);
        const key = `${clientId}-${accountId}`;
        rows.push(
          <Collapsible key={key} asChild open={expanded[key]} onOpenChange={(open) => setExpanded({ ...expanded, [key]: open })}>
            <>
              <TableRow className="border-b">
                <TableCell className="w-12 px-2">
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      {expanded[key] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </Button>
                  </CollapsibleTrigger>
                </TableCell>
                <TableCell className="font-medium">{clientPortfolio?.clientName || clientId}</TableCell>
                <TableCell className="font-medium">{account?.accountNumberCustodian || accountId}</TableCell>
                <TableCell>{(account as any)?.custodian || "-"}</TableCell>
                <TableCell colSpan={7}>
                  <span className="text-sm text-muted-foreground">{stmts.length} {stmts.length === 1 ? "statement" : "statements"}</span>
                </TableCell>
              </TableRow>
              <CollapsibleContent asChild>
                <TableRow>
                  <TableCell colSpan={11} className="p-0">
                    <div className="bg-muted/30">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Period</TableHead>
                            <TableHead className="text-right">Start Value</TableHead>
                            <TableHead className="text-right">End Value</TableHead>
                            <TableHead className="text-right">Deposits</TableHead>
                            <TableHead className="text-right">Withdrawals</TableHead>
                            <TableHead className="text-right">Return</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {stmts.map((stmt) => {
                            const isPlaid = stmt.source === "PLAID";
                            const hasValues = stmt.startingValue != null && stmt.endingValue != null;
                            const netDeposits = hasValues
                              ? stmt.deposits - stmt.withdrawals - stmt.fees + stmt.dividendsEarned + stmt.interestEarned
                              : 0;
                            const twr = hasValues && stmt.startingValue > 0
                              ? (isPlaid
                                  ? ((stmt.endingValue - stmt.startingValue) / stmt.startingValue) * 100
                                  : ((stmt.endingValue - netDeposits) / stmt.startingValue - 1) * 100)
                              : null;
                            return (
                              <TableRow key={stmt.id}>
                                <TableCell>{new Date(stmt.statementDate).toLocaleDateString("en-US", { month: "short", year: "numeric" })}</TableCell>
                                <TableCell className="text-right">{stmt.startingValue != null ? `$${stmt.startingValue.toLocaleString()}` : "-"}</TableCell>
                                <TableCell className="text-right">{stmt.endingValue != null ? `$${stmt.endingValue.toLocaleString()}` : "-"}</TableCell>
                                <TableCell className="text-right">${stmt.deposits.toLocaleString()}</TableCell>
                                <TableCell className="text-right">${stmt.withdrawals.toLocaleString()}</TableCell>
                                <TableCell className="text-right">
                                  {twr != null ? (
                                    <span className={twr >= 0 ? "text-green-600" : "text-red-600"}>{twr >= 0 ? "+" : ""}{twr.toFixed(2)}%</span>
                                  ) : (
                                    "-"
                                  )}
                                </TableCell>
                                <TableCell className="text-right">
                                  <div className="flex justify-end gap-1">
                                    <Button variant="ghost" size="sm" onClick={() => setEditing({ ...stmt })}>Edit</Button>
                                    <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => setDeleting(stmt.id)}>Delete</Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </TableCell>
                </TableRow>
              </CollapsibleContent>
            </>
          </Collapsible>
        );
      }
    }
    return rows;
  }

  const [addingTransactionClientId, setAddingTransactionClientId] = useState<string | null>(null);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [transactionForm, setTransactionForm] = useState({
    transactionDate: "",
    accountNumber: "",
    transactionType: "BUY",
    action: "",
    symbol: "",
    payment: "Cash",
    currency: "USD",
    quantity: "",
    price: "",
    commission: "",
    fees: "",
    interest: "",
    amount: "",
  });

  const handleAddPosition = async (accountId: string) => {
    const sym = prompt("Enter symbol:");
    if (!sym) return;
    const name = prompt("Enter name:") || sym;
    const shares = parseFloat(prompt("Enter shares:") || "0");
    if (shares <= 0) return;
    const price = parseFloat(prompt("Enter price:") || "0");
    if (price <= 0) return;
    try {
      await portfolioService.updatePortfolioPosition(accountId, {
        id: "",
        symbol: sym.toUpperCase(),
        name,
        shares,
        avgCost: price,
        currentPrice: price,
        marketValue: shares * price,
        change: 0,
        totalReturn: 0,
        gainLoss: null,
        gainLossPercent: null,
        sector: "Diversified",
        assetType: "STOCK",
      });
      setClientPortfolios((prev) => prev.map((cp) => ({
        ...cp,
        portfolioAccounts: cp.portfolioAccounts.map((a) =>
          a.id === accountId
            ? { ...a, positions: [...(a.positions || []), { id: "", symbol: sym.toUpperCase(), name, shares, avgCost: price, currentPrice: price, marketValue: shares * price, change: 0, totalReturn: 0, gainLoss: null, gainLossPercent: null, sector: "Diversified", assetType: "STOCK" }] }
            : a
        ),
      })));
    } catch (error) {
      console.error("Failed to add position:", error);
    }
  };

  const handleSaveEditedPosition = async () => {
    if (!editingPosition || !editingPositionAccountId) return;
    try {
      await portfolioService.updatePortfolioPosition(editingPositionAccountId, editingPosition);
      setClientPortfolios((prev) => prev.map((cp) => ({
        ...cp,
        portfolioAccounts: cp.portfolioAccounts.map((a) =>
          a.id === editingPositionAccountId
            ? { ...a, positions: a.positions.map((p) => p.id === editingPosition.id ? editingPosition : p) }
            : a
        ),
      })));
      setEditingPosition(null);
      setEditingPositionAccountId("");
    } catch (error) {
      console.error("Failed to update position:", error);
    }
  };

  const handleDeletePosition = async () => {
    if (!deletingPosition || !deletingPositionAccountId) return;
    try {
      await portfolioService.deletePortfolioPosition(deletingPositionAccountId, deletingPosition.id);
      setClientPortfolios((prev) => prev.map((cp) => ({
        ...cp,
        portfolioAccounts: cp.portfolioAccounts.map((a) =>
          a.id === deletingPositionAccountId
            ? { ...a, positions: a.positions.filter((p) => p.id !== deletingPosition.id) }
            : a
        ),
      })));
      setDeletingPosition(null);
      setDeletingPositionAccountId("");
    } catch (error) {
      console.error("Failed to delete position:", error);
    }
  };

  const handleEditAccount = async (account: PortfolioAccountResponse) => {
    setIsSavingAccount(true);
    try {
      const { id, ...accountData } = account;
      const payload = {
        ...accountData,
        accountType: account.accountType.toLowerCase(),
        ownershipType: account.ownershipType.toLowerCase(),
        status: account.status.toLowerCase(),
        positions: (account.positions || []).map((pos, idx) => ({
          ...pos,
          delete: deletedPositionIndices.includes(idx),
        })),
      };
      await portfolioService.updatePortfolio(id, payload);
      setClientPortfolios((prev) => prev.map((cp) => ({
        ...cp,
        portfolioAccounts: cp.portfolioAccounts.map((a) =>
          a.id === id ? account : a
        ),
      })));
      setEditingAccount(null);
      setDeletedPositionIndices([]);
    } catch (error) {
      console.error("Failed to update portfolio:", error);
    } finally {
      setIsSavingAccount(false);
    }
  };

  const handleAddPositionToEdit = () => {
    if (!newPosition.symbol || !newPosition.shares || !newPosition.avgCost || !editingAccount) return;
    const position: PortfolioPositionResponse = {
      id: "",
      symbol: newPosition.symbol.toUpperCase(),
      name: newPosition.name || newPosition.symbol,
      shares: parseFloat(newPosition.shares),
      avgCost: parseFloat(newPosition.avgCost),
      currentPrice: 0,
      marketValue: 0,
      change: 0,
      totalReturn: 0,
      sector: newPosition.sector || "Diversified",
      assetType: "STOCK",
      gainLoss: null,
      gainLossPercent: null,
    };
    setEditingAccount({ ...editingAccount, positions: [...(editingAccount.positions || []), position] });
    setNewPosition({ symbol: "", name: "", shares: "", avgCost: "", sector: "" });
  };

  const handleRemovePositionFromEdit = (index: number) => {
    if (!editingAccount) return;
    setDeletedPositionIndices((prev) => [...prev, index]);
    setEditingAccount({
      ...editingAccount,
      positions: (editingAccount.positions || []).map((pos, i) =>
        i === index ? { ...pos, delete: true } : pos
      ),
    });
  };

  const handleAddTransaction = async () => {
    if (!transactionForm.accountNumber || !transactionForm.transactionDate) return;
    const clientCp = clientPortfolios.find(cp => cp.clientId === addingTransactionClientId);
    const account = clientCp?.portfolioAccounts.find(a => a.id === transactionForm.accountNumber);
    if (!account) return;
    try {
      const payload: CreateTransactionRequest = {
        accountId: account.id,
        transactionType: transactionForm.transactionType,
        transactionDate: transactionForm.transactionDate,
        securitySymbol: transactionForm.symbol || undefined,
        quantity: transactionForm.quantity ? Number(transactionForm.quantity) : undefined,
        price: transactionForm.price ? Number(transactionForm.price) : undefined,
        amount: transactionForm.amount ? Number(transactionForm.amount) : (Number(transactionForm.price) || 0) * (Number(transactionForm.quantity) || 0),
        fees: transactionForm.fees ? Number(transactionForm.fees) : undefined,
        commission: transactionForm.commission ? Number(transactionForm.commission) : undefined,
        interest: transactionForm.interest ? Number(transactionForm.interest) : undefined,
        currency: transactionForm.currency || undefined,
        description: transactionForm.action || undefined,
        source: "manual",
      };
      await transactionService.createTransaction(addingTransactionClientId, payload);
      setAddingTransactionClientId(null);
      setTransactionForm({
        transactionDate: "", accountNumber: "", transactionType: "BUY", action: "",
        symbol: "", payment: "Cash", currency: "USD", quantity: "", price: "",
        commission: "", fees: "", interest: "", amount: "",
      });
      fetchTransactions();
    } catch (error) {
      console.error("Failed to create transaction:", error);
    }
  };

  useEffect(() => {
    const fetchClientPortfolios = async () => {
      if (!userId) return;
      setIsClientPortfoliosLoading(true);
      try {
        const response = await portfolioService.getAllPortfoliosForClients(userId);
        const data = Array.isArray(response) ? response : (response?.data || []);
        setClientPortfolios(data);
      } catch (error) {
        console.error("Failed to fetch client portfolios:", error);
      } finally {
        setIsClientPortfoliosLoading(false);
      }
    };
    fetchClientPortfolios();
  }, [userId]);

  const fetchMeetings = async () => {
    if (!userId) return;
    try {
      const [pendingRes, acceptedRes] = await Promise.all([
        meetingService.getMyMeetings(userId, "PENDING", false, ''),
        meetingService.getMyMeetings(userId, "ACCEPTED", false, '')
      ]);
      const pMeetings = pendingRes.data || (Array.isArray(pendingRes) ? pendingRes : []);
      const aMeetings = acceptedRes.data || (Array.isArray(acceptedRes) ? acceptedRes : []);
      const allMeetings = [...pMeetings, ...aMeetings].sort((a, b) =>
        new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
      );
      setMeetings(allMeetings);
    } catch (error) {
      console.error("Failed to fetch meetings:", error);
    }
  };

  const handleAcceptMeeting = async (meetingId) => {
    if (!userId) return;
    try {
      await meetingService.updateMeetingStatus(meetingId, userId, "ACCEPTED");
      toast.success("Meeting accepted");
      fetchMeetings();
    } catch (e) { toast.error(e.message || "Failed to accept"); }
  };

  const handleDeclineMeeting = async (meetingId) => {
    if (!userId) return;
    try {
      await meetingService.updateMeetingStatus(meetingId, userId, "DECLINED");
      toast.success("Meeting declined");
      fetchMeetings();
    } catch (e) { toast.error(e.message || "Failed to decline"); }
  };

  const handleCancelMeeting = async (meetingId) => {
    if (!userId) return;
    try {
      await meetingService.updateMeetingStatus(meetingId, userId, "CANCELLED");
      toast.success("Meeting cancelled");
      fetchMeetings();
    } catch (e) { toast.error(e.message || "Failed to cancel"); }
  };

  useEffect(() => {
    if (userId) fetchMeetings();
  }, [userId]);

  const fetchTransactions = async () => {
    if (!userId || clientPortfolios.length === 0) return;
    setIsTransactionsLoading(true);
    const results: Record<string, TransactionResponse[]> = {};
    try {
      await Promise.all(clientPortfolios.map(async (cp) => {
        try {
          const response: any = await transactionService.getRecentTransactions(cp.clientId);
          results[cp.clientId] = Array.isArray(response) ? response : (response?.data || []);
        } catch (error) {
          console.error(`Failed to fetch transactions for client ${cp.clientId}:`, error);
          results[cp.clientId] = [];
        }
      }));
      setClientTransactions(results);
    } catch (error) {
      console.error("Failed to fetch transactions:", error);
    } finally {
      setIsTransactionsLoading(false);
    }
  };

  useEffect(() => {
    if (clientPortfolios.length > 0) {
      fetchClientAccountStatements();
      fetchTransactions();
    }
  }, [clientPortfolios]);

  const fetchClientGrowthData = async () => {
    if (!userId) return;
    try {
      const now = new Date();
      const endMonth = now.getMonth() ;
      const endYear = now.getFullYear();
      const startDate = new Date(now.getFullYear(), now.getMonth() - 6, 1);
      const startMonth = startDate.getMonth() + 1;
      const startYear = startDate.getFullYear();

      const response = await advisorService.getClientGrowthAndAUM(userId, startMonth, startYear, endMonth, endYear);
      console.log("Raw API response for client growth:", response);
      const rawData = response.data || response.content || response.dataPoints ||response;
      const data = Array.isArray(rawData) ? rawData : [];
      console.log("Extracted data array:", data);

      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const mapped = data.map((item: any) => {
        const monthNum = Number(item.month);
        return {
          month: monthNum >= 1 && monthNum <= 12 ? monthNames[monthNum - 1] : `M${item.month}`,
          clients: Number(item.clientCount) || 0,
          aum: Number(item.aum) || 0,
        };
      });
      console.log("Mapped chart data:", mapped);
      setClientGrowthData(mapped);
    } catch (error) {
      console.error("Failed to fetch client growth data:", error);
    }
  };

  useEffect(() => {
    if (userId) fetchClientGrowthData();
  }, [userId]);

  const fetchActivities = async () => {
    if (!userId) return;
    try {
      const response = await activityLogService.getActivityLogsByUser(userId);
      const data = response.data || (Array.isArray(response) ? response : []);
      setRecentActivities(data);
    } catch (error) {
      console.error("Failed to fetch activities:", error);
    }
  };

  useEffect(() => {
    if (userId) fetchActivities();
  }, [userId]);

  const totalAUM = clientPortfolios.reduce((sum, cp) =>
    sum + cp.portfolioAccounts.reduce((accSum, acc) => accSum + (acc.balance || 0), 0), 0);
  const totalClients = clientPortfolios.length;
  const clientReturns = clientPortfolios.map(cp => {
    const allPositions = cp.portfolioAccounts.flatMap(acc => acc.positions || []);
    const totalValue = allPositions.reduce((sum, p) => sum + (p.marketValue || 0), 0);
    const totalCost = allPositions.reduce((sum, p) => sum + ((p.avgCost || 0) * (p.shares || 0)), 0);
    return totalCost > 0 ? ((totalValue - totalCost) / totalCost) * 100 : 0;
  });
  const avgReturn = clientReturns.length > 0
    ? clientReturns.reduce((sum, r) => sum + r, 0) / clientReturns.length
    : 0;
  const returnClassName = (value: number) =>
    value > 0 ? "text-green-600" : value < 0 ? "text-red-600" : "text-muted-foreground";
  const formatReturn = (value: number, digits = 1) =>
    `${value > 0 ? "+" : ""}${value.toFixed(digits)}%`;
  const totalAlerts = alerts.filter(a => a.severity === "High" || a.severity === "Medium").length;
  const aggregateMetrics: AggregateMetrics[] = Object.entries(
    clientPortfolios.reduce<Record<string, { count: number; totalValue: number; returns: number[] }>>((acc, cp) => {
      const risk = cp.investorProfile?.riskTolerance || "Other";
      if (!acc[risk]) acc[risk] = { count: 0, totalValue: 0, returns: [] };
      acc[risk].count++;
      acc[risk].totalValue += cp.portfolioAccounts.reduce((s, a) => s + (a.balance || 0), 0);
      const allPositions = cp.portfolioAccounts.flatMap(a => a.positions || []);
      const posValue = allPositions.reduce((s, p) => s + (p.marketValue || 0), 0);
      const posCost = allPositions.reduce((s, p) => s + ((p.avgCost || 0) * (p.shares || 0)), 0);
      acc[risk].returns.push(posCost > 0 ? ((posValue - posCost) / posCost) * 100 : 0);
      return acc;
    }, {})
  ).map(([category, data]) => ({
    category,
    count: data.count,
    totalValue: data.totalValue,
    avgReturn: data.returns.length > 0 ? data.returns.reduce((s, r) => s + r, 0) / data.returns.length : 0,
  }));

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case "Taxable": return Wallet;
      case "Retirement": return PiggyBank;
      case "Education": return GraduationCap;
      case "Health": return Heart;
      case "Cash": return Wallet;
      case "Other": return Briefcase;
      default: return Briefcase;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "Taxable": return "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300";
      case "Retirement": return "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300";
      case "Education": return "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300";
      case "Health": return "bg-pink-100 text-pink-700 dark:bg-pink-900 dark:text-pink-300";
      case "Cash": return "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300";
      default: return "bg-gray-100 text-gray-700 dark:bg-gray-900 dark:text-gray-300";
    }
  };

  return (
    <div className="space-y-6">
      {isClientPortfoliosLoading && (
        <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-800 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-200">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading portfolio...
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl">Client Portfolio Management</h2>
          <p className="text-muted-foreground">
            Comprehensive view of all client accounts and performance
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={selectedClientId} onValueChange={setSelectedClientId}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Select a client" />
            </SelectTrigger>
            <SelectContent>
              {clientPortfolios.map((cp) => (
                <SelectItem key={cp.clientId} value={cp.clientId}>
                  {cp.clientName || cp.clientId}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            disabled={!selectedClientId}
            onClick={() => setIsScheduleMeetingOpen(true)}
          >
            <Calendar className="mr-2 h-4 w-4" />
            Schedule Meeting
          </Button>
          <Button onClick={() => setIsInviteOpen(true)}>
            <Users className="mr-2 h-4 w-4" />
            Invite New Client
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Total AUM
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl">${(totalAUM / 1000000).toFixed(2)}M</div>
            <p className="text-xs text-muted-foreground">Total across all clients</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Users className="h-4 w-4" />
              Total Clients
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl">{totalClients}</div>
            <p className="text-xs text-muted-foreground">
              {clientPortfolios.reduce((sum, cp) => sum + cp.portfolioAccounts.length, 0)} total accounts
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              {avgReturn >= 0 ? (
                <TrendingUp className="h-4 w-4" />
              ) : (
                <TrendingDown className="h-4 w-4" />
              )}
              Avg Portfolio Return
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl ${returnClassName(avgReturn)}`}>
              {formatReturn(avgReturn)}
            </div>
            <p className="text-xs text-muted-foreground">Average return across all accounts</p>
          </CardContent>
        </Card>

      </div>

      {/* Dashboard Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Client Growth */}
        <Card>
          <CardHeader>
            <CardTitle>Client Growth & AUM</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={clientGrowthData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}K`} />
                <Tooltip formatter={(value: number, name: string) => name === "AUM" ? [`$${value.toLocaleString()}`, name] : [value, name]} />
                <Legend />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="clients"
                  stroke="#3b82f6"
                  name="Total Clients"
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="aum"
                  stroke="#10b981"
                  name="AUM"
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Performance by Risk Profile */}
        <Card>
          <CardHeader>
            <CardTitle>Performance by Risk Profile</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={aggregateMetrics}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="category" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="avgReturn" fill="#3b82f6" name="Avg Return %" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Meetings and Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Meetings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Upcoming Meetings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {meetings.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground bg-muted/20 rounded-lg border border-dashed">
                  <p>No upcoming meetings</p>
                </div>
              ) : (
                meetings.map((meeting) => {
                  const dateStr = meeting.startTime.endsWith('Z') || meeting.startTime.includes('+')
                    ? meeting.startTime
                    : `${meeting.startTime}Z`;
                  const formattedTime = new Date(dateStr).toLocaleString([], {
                    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
                  });
                  const clientName = meeting.hostId === userId
                    ? clientPortfolios.find(c => c.clientId === meeting.participantId)?.clientName || meeting.participantId
                    : meeting.hostFullName || meeting.hostId;
                  return (
                    <div key={meeting.meetingId || meeting.id} className="flex items-center justify-between border-b pb-3 last:border-0 last:pb-0">
                      <div className="flex items-center gap-3">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <div className="font-medium">{clientName}</div>
                          <div className="text-xs text-muted-foreground">{MEETING_TYPES.find(mt => mt.value === meeting.meetingType?.toLowerCase())?.label || meeting.title || "Meeting"}</div>
                        </div>
                      </div>
                      <div className="text-right flex items-center gap-2">
                        <div>
                          <div className="text-sm">{formattedTime}</div>
                          <Badge variant={meeting.status === "ACCEPTED" ? "default" : "secondary"} className="text-xs mt-1">
                            {meeting.status === "ACCEPTED" ? "Accepted" : meeting.status === "PENDING" ? "Pending" : meeting.status}
                          </Badge>
                        </div>
                        {meeting.status === "PENDING" && meeting.hostId !== userId && (
                          <div className="flex flex-col gap-1">
                            <Button size="sm" className="h-7 text-xs bg-green-600 hover:bg-green-700" onClick={() => handleAcceptMeeting(meeting.meetingId || meeting.id)}>Accept</Button>
                            <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 border-red-200" onClick={() => handleDeclineMeeting(meeting.meetingId || meeting.id)}>Decline</Button>
                          </div>
                        )}
                        {meeting.status === "ACCEPTED" && (
                          <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 border-red-200" onClick={() => handleCancelMeeting(meeting.meetingId || meeting.id)}>Cancel</Button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Recent Activity
            </CardTitle>
            <Button size="sm" onClick={() => setIsLogActivityOpen(true)}>
              Log Activity
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentActivities.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground bg-muted/20 rounded-lg border border-dashed">
                  <p>No recent activity</p>
                </div>
              ) : (
                recentActivities.map((activity: any, idx: number) => {
                  const timeStr = activity.createdAt || activity.timestamp
                    ? new Date(activity.createdAt || activity.timestamp).toLocaleString()
                    : "";
                  const clientName = activity.recipientUserId
                    ? clientPortfolios.find(c => c.clientId === activity.recipientUserId)?.clientName || activity.recipientUserName || activity.recipientUserId
                    : "";
                  return (
                    <div key={activity.id || idx} className="flex items-center justify-between border-b pb-3 last:border-0 last:pb-0">
                      <div>
                        <div className="font-medium">{clientName}</div>
                        <div className="text-sm text-muted-foreground">{activity.description || activity.activityType}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-muted-foreground">{timeStr}</div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>

        {/* Log Activity Dialog */}
        <Dialog open={isLogActivityOpen} onOpenChange={setIsLogActivityOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Log Activity</DialogTitle>
              <DialogDescription>Record an activity for a client</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Activity Type</Label>
                <Select
                  value={activityForm.activityType}
                  onValueChange={(v) => setActivityForm({ ...activityForm, activityType: v })}
                >
                  <SelectTrigger><SelectValue placeholder="Select activity type" /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(ACTIVITY_TYPES_ADVISOR).map(([key, label]) => (
                      <SelectItem key={key} value={key}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Client (optional)</Label>
                <Select
                  value={activityForm.recipientUserId}
                  onValueChange={(v) => setActivityForm({ ...activityForm, recipientUserId: v === "none" ? "" : v })}
                >
                  <SelectTrigger><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {clientPortfolios.map((cp) => (
                      <SelectItem key={cp.clientId} value={cp.clientId}>
                        {cp.clientName || cp.clientId}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={activityForm.description}
                  onChange={(e) => setActivityForm({ ...activityForm, description: e.target.value })}
                  placeholder="Describe the activity..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsLogActivityOpen(false)}>Cancel</Button>
              <Button onClick={async () => {
                if (!activityForm.activityType) return;
                try {
                  await activityLogService.logActivity({ ...activityForm, userId: userId || "" }, null);
                  toast.success("Activity logged successfully");
                  setIsLogActivityOpen(false);
                  setActivityForm({ userId: userId || "", userName: user?.name || "", activityType: "", description: "", recipientUserId: "", recipientUserName: "", metadata: {} });
                  fetchActivities();
                } catch (error) {
                  toast.error("Failed to log activity");
                }
              }}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium">Client Accounts & Holdings</h3>
        </div>

        {isClientPortfoliosLoading ? (
          <Card>
            <CardContent className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading client portfolios...
            </CardContent>
          </Card>
        ) : clientPortfolios.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              No client portfolios found
            </CardContent>
          </Card>
        ) : (
          <Tabs defaultValue="accounts">
            <TabsList>
              <TabsTrigger value="accounts">Accounts & Positions</TabsTrigger>
              <TabsTrigger value="allocation">Allocation</TabsTrigger>
              <TabsTrigger value="transactions">Transactions</TabsTrigger>
              <TabsTrigger value="statements">Statements</TabsTrigger>
              <TabsTrigger value="performance">Performance Overview</TabsTrigger>
            </TabsList>

            {/* Client Accounts & Positions */}
            <TabsContent value="accounts" className="space-y-4">
              {clientPortfolios.map((cp) => {
                const clientAccountsByCategory = cp.portfolioAccounts.reduce((acc, account) => {
                  console.log("Account:", account);
                  console.log("Account Type:", account.accountType);
                  const accountTypeInfo = ACCOUNT_TYPES[(account.accountType?.toUpperCase() || "") as keyof typeof ACCOUNT_TYPES];
                  const category = accountTypeInfo?.category || "Other";
                  if (!acc[category]) acc[category] = [];
                  acc[category].push(account);
                  return acc;
                }, {} as Record<string, PortfolioAccountResponse[]>);

                return (
                  <Collapsible
                    key={cp.clientId}
                    asChild
                    open={expandedClients[cp.clientId]}
                    onOpenChange={(open) =>
                      setExpandedClients({ ...expandedClients, [cp.clientId]: open })
                    }
                  >
                    <Card>
                      <CollapsibleTrigger asChild>
                        <CardHeader className="cursor-pointer hover:bg-muted/50">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {expandedClients[cp.clientId] ? (
                                <ChevronDown className="h-5 w-5 text-muted-foreground" />
                              ) : (
                                <ChevronRight className="h-5 w-5 text-muted-foreground" />
                              )}
                              <div>
                                <CardTitle className="text-base">Client: {cp.clientName}</CardTitle>
                                <p className="text-sm text-muted-foreground">
                                  {cp.portfolioAccounts.length} account{cp.portfolioAccounts.length !== 1 ? "s" : ""}
                                </p>
                              </div>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); setAddingAccountClientId(cp.clientId); }}
                            >
                              <Plus className="h-4 w-4 mr-1" />
                              Add Account
                            </Button>
                          </div>
                        </CardHeader>
                      </CollapsibleTrigger>
                      <CollapsibleContent asChild>
                        <CardContent className="space-y-6">
                          {(Object.entries(clientAccountsByCategory) as [string, PortfolioAccountResponse[]][]).map(([category, categoryAccounts]) => {
                            const Icon = getCategoryIcon(category);
                            const categoryBalance = categoryAccounts.reduce((sum, a) => sum + a.balance, 0);
                            return (
                              <div key={category}>
                                <div className="flex items-center gap-2 mb-3">
                                  <div className={`p-1.5 rounded-lg ${getCategoryColor(category)}`}>
                                    <Icon className="h-4 w-4" />
                                  </div>
                                  <span className="text-sm font-medium">{category}</span>
                                  <span className="text-xs text-muted-foreground">
                                    {categoryAccounts.length} account{categoryAccounts.length !== 1 ? "s" : ""} &bull; ${(categoryBalance / 1000).toFixed(0)}K
                                  </span>
                                </div>
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead className="w-12 px-2"></TableHead>
                                      <TableHead className="px-4 text-left">Account #</TableHead>
                                      <TableHead className="px-4 text-left">Custodian</TableHead>
                                      <TableHead className="px-4 text-left">Custodian Account #</TableHead>
                                      <TableHead className="px-4 text-left">Type</TableHead>
                                      <TableHead className="px-4 text-left">Ownership</TableHead>
                                      <TableHead className="text-right px-4">Balance</TableHead>
                                      <TableHead className="text-right px-4">Cash</TableHead>
                                      <TableHead className="text-right px-4">YTD Contributions</TableHead>
                                      <TableHead className="text-left px-4">Status</TableHead>
                                      <TableHead className="text-left px-4">Actions</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {categoryAccounts.map((account) => {
                                      const isAccountExpanded = expandedClients[`${cp.clientId}-${account.id}`];
                                      return (
                                        <Collapsible key={account.id} asChild open={isAccountExpanded} onOpenChange={(open) => setExpandedClients({ ...expandedClients, [`${cp.clientId}-${account.id}`]: open })}>
                                          <>
                                            <TableRow className="border-b">
                                              <TableCell className="w-12 px-2">
                                                <CollapsibleTrigger asChild>
                                                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                                    {isAccountExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                                  </Button>
                                                </CollapsibleTrigger>
                                              </TableCell>
                                              <TableCell className="px-4">
                                                <div className="font-medium">{account.accountName}</div>
                                                <div className="text-xs text-muted-foreground">
                                                  {account.accountNumber} &bull; {account.positions?.length || 0} positions
                                                </div>
                                              </TableCell>
                                              <TableCell className="px-4">
                                                <span className="text-sm">{(account as any).custodian || "-"}</span>
                                              </TableCell>
                                              <TableCell className="px-4">
                                                <span className="text-sm">{account.accountNumberCustodian || "-"}</span>
                                              </TableCell>
                                              <TableCell className="px-4">
                                                <Badge variant="outline">
                                                  {ACCOUNT_TYPES[(account.accountType?.toUpperCase() || "") as keyof typeof ACCOUNT_TYPES]?.name || account.accountType}
                                                </Badge>
                                              </TableCell>
                                              <TableCell className="px-4">
                                                <span className="text-sm text-muted-foreground">
                                                  {OWNERSHIP_TYPES[account.ownershipType as keyof typeof OWNERSHIP_TYPES] || account.ownershipType}
                                                </span>
                                              </TableCell>
                                              <TableCell className="text-right px-4">${account.balance.toLocaleString()}</TableCell>
                                              <TableCell className="text-right px-4">${account.cashBalance.toLocaleString()}</TableCell>
                                              <TableCell className="text-right px-4">${(account.contributions || 0).toLocaleString()}</TableCell>
                                              <TableCell className="px-4">
                                                <Badge variant={account.status === "Active" ? "secondary" : "outline"}>
                                                  {account.status}
                                                </Badge>
                                              </TableCell>
                                              <TableCell className="px-4">
                                                <div className="flex gap-1">
                                                  <Dialog>
                                                    <DialogTrigger asChild>
                                                      <Button variant="ghost" size="sm">Details</Button>
                                                    </DialogTrigger>
                                                    <DialogContent className="max-w-3xl">
                                                      <DialogHeader>
                                                        <DialogTitle>{account.accountName}</DialogTitle>
                                                        <DialogDescription>
                                                            {ACCOUNT_TYPES[(account.accountType?.toUpperCase() || "") as keyof typeof ACCOUNT_TYPES]?.name || account.accountType} &bull; {OWNERSHIP_TYPES[account.ownershipType as keyof typeof OWNERSHIP_TYPES] || account.ownershipType}
                                                        </DialogDescription>
                                                      </DialogHeader>
                                                      <div className="space-y-4">
                                                        <div className="grid grid-cols-2 gap-4">
                                                          <div>
                                                            <Label className="text-xs text-muted-foreground">Account Number</Label>
                                                            <p className="text-sm">{account.accountNumber}</p>
                                                          </div>
                                                          <div>
                                                            <Label className="text-xs text-muted-foreground">Opened Date</Label>
                                                            <p className="text-sm">{account.openedDate || "-"}</p>
                                                          </div>
                                                          <div>
                                                            <Label className="text-xs text-muted-foreground">Current Balance</Label>
                                                            <p className="text-sm">${account.balance.toLocaleString()}</p>
                                                          </div>
                                                          <div>
                                                            <Label className="text-xs text-muted-foreground">YTD Return</Label>
                                                            <p className={`text-sm ${(account.ytdReturn ?? 0) >= 0 ? "text-green-600" : "text-red-600"}`}>
                                                              {(account.ytdReturn ?? 0) >= 0 ? "+" : ""}{account.ytdReturn ?? 0}%
                                                            </p>
                                                          </div>
                                                          <div>
                                                            <Label className="text-xs text-muted-foreground">Cash Balance</Label>
                                                            <p className="text-sm">${account.cashBalance.toLocaleString()}</p>
                                                          </div>
                                                          <div>
                                                            <Label className="text-xs text-muted-foreground">Custodian</Label>
                                                            <p className="text-sm">{(account as any).custodian || "-"}</p>
                                                          </div>
                                                          <div>
                                                            <Label className="text-xs text-muted-foreground">Custodian Account #</Label>
                                                            <p className="text-sm">{account.accountNumberCustodian || "-"}</p>
                                                          </div>
                                                          <div>
                                                            <Label className="text-xs text-muted-foreground">Status</Label>
                                                            <p className="text-sm">{account.status}</p>
                                                          </div>
                                                        </div>
                                                      </div>
                                                    </DialogContent>
                                                  </Dialog>
                                                  <Button variant="outline" size="sm" onClick={() => setEditingAccount(account)}>
                                                    <Pencil className="h-3.5 w-3.5 mr-1" />
                                                    Edit
                                                  </Button>
                                                </div>
                                              </TableCell>
                                            </TableRow>
                                            <CollapsibleContent asChild>
                                              <TableRow>
                                                <TableCell colSpan={9} className="bg-muted/30 p-0">
                                                    <div className="p-4">
                                                    <Table>
                                                      <TableHeader>
                                                        <TableRow>
                                                          <TableHead>Symbol</TableHead>
                                                          <TableHead>Name</TableHead>
                                                          <TableHead>Sector</TableHead>
                                                          <TableHead className="text-right">Shares</TableHead>
                                                          <TableHead className="text-right">Avg Cost</TableHead>
                                                          <TableHead className="text-right">Total Cost</TableHead>
                                                          <TableHead className="text-right">Current Price</TableHead>
                                                          <TableHead className="text-right">Market Value</TableHead>
                                                          <TableHead className="text-right">Today</TableHead>
                                                          <TableHead className="text-right">Total Return</TableHead>
                                                          <TableHead className="text-right">Actions</TableHead>
                                                        </TableRow>
                                                      </TableHeader>
                                                      <TableBody>
                                                        {account.positions.map((pos) => (
                                                          <TableRow key={pos.id}>
                                                            <TableCell className="font-medium">
                                                              <a href={`https://finance.yahoo.com/quote/${pos.symbol}/`} target="_blank" rel="noopener noreferrer" className="font-bold text-blue-600 hover:text-blue-800 hover:underline">{pos.symbol}</a>
                                                            </TableCell>
                                                            <TableCell className="text-muted-foreground">{pos.name}</TableCell>
                                                            <TableCell className="text-xs text-muted-foreground">{pos.sector}</TableCell>
                                                            <TableCell className="text-right">{pos.shares}</TableCell>
                                                            <TableCell className="text-right">${pos.avgCost.toFixed(2)}</TableCell>
                                                            <TableCell className="text-right">${(pos.shares * pos.avgCost).toLocaleString()}</TableCell>
                                                            <TableCell className="text-right">${pos.currentPrice.toFixed(2)}</TableCell>
                                                            <TableCell className="text-right">
                                                              ${pos.marketValue.toLocaleString()}
                                                              {pos.gainLoss != null && (
                                                                <div className={`text-xs ${pos.gainLoss >= 0 ? "text-green-600" : "text-red-600"}`}>
                                                                  {pos.gainLoss >= 0 ? "+" : ""}${pos.gainLoss.toLocaleString()}
                                                                </div>
                                                              )}
                                                            </TableCell>
                                                            <TableCell className="text-right">
                                                              <span className={pos.change >= 0 ? "text-green-600" : "text-red-600"}>
                                                                {pos.change >= 0 ? "+" : ""}{pos.change.toFixed(2)}%
                                                              </span>
                                                            </TableCell>
                                                            <TableCell className="text-right">
                                                              <span className={pos.totalReturn >= 0 ? "text-green-600" : "text-red-600"}>
                                                                {pos.totalReturn >= 0 ? "+" : ""}{pos.totalReturn.toFixed(2)}%
                                                              </span>
                                                            </TableCell>
                                                            <TableCell className="text-right">
                                                              <div className="flex justify-end gap-1">
                                                                <Button variant="ghost" size="sm" onClick={() => { setEditingPosition({ ...pos }); setEditingPositionAccountId(account.id); }}>
                                                                  <Pencil className="h-3 w-3" />
                                                                </Button>
                                                                <Button variant="ghost" size="sm" className="text-red-600" onClick={() => { setDeletingPosition(pos); setDeletingPositionAccountId(account.id); }}>
                                                                  <Trash2 className="h-3 w-3" />
                                                                </Button>
                                                              </div>
                                                            </TableCell>
                                                          </TableRow>
                                                        ))}
                                                      </TableBody>
                                                    </Table>
                                                  </div>
                                                </TableCell>
                                              </TableRow>
                                            </CollapsibleContent>
                                          </>
                                        </Collapsible>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              </div>
                            );
                          })}
                        </CardContent>
                      </CollapsibleContent>
                    </Card>
                  </Collapsible>
                );
              })}
            </TabsContent>

            {/* Allocation Tab */}
            <TabsContent value="allocation" className="space-y-4">
              {clientPortfolios.length === 0 ? (
                <Card>
                  <CardContent className="py-8 text-center text-muted-foreground">
                    No client data available
                  </CardContent>
                </Card>
              ) : (
                clientPortfolios.map((cp) => {
                  const allPositions = cp.portfolioAccounts.flatMap(acc => acc.positions || []);
                  const totalValue = allPositions.reduce((sum, p) => sum + p.marketValue, 0);
                  const sectorTotals = allPositions.reduce((acc, pos) => {
                    acc[pos.sector] = (acc[pos.sector] || 0) + pos.marketValue;
                    return acc;
                  }, {} as Record<string, number>);
                  const sectorAllocationData = Object.entries(sectorTotals)
                    .map(([sector, value]) => ({
                      sector,
                      value: totalValue > 0 ? (value / totalValue) * 100 : 0,
                      amount: value,
                    }))
                    .sort((a, b) => b.value - a.value);

                  const categoryAccounts = cp.portfolioAccounts.reduce((acc, account) => {
                    const accountTypeInfo = ACCOUNT_TYPES[(account.accountType?.toUpperCase() || "") as keyof typeof ACCOUNT_TYPES];
                    const category = accountTypeInfo?.category || "Other";
                    if (!acc[category]) acc[category] = [];
                    acc[category].push(account);
                    return acc;
                  }, {} as Record<string, PortfolioAccountResponse[]>);
                  const totalCategoryBalance = cp.portfolioAccounts.reduce((sum, a) => sum + a.balance, 0);

                  return (
                    <Collapsible
                      key={cp.clientId}
                      asChild
                      open={expandedClients[`alloc-${cp.clientId}`]}
                      onOpenChange={(open) =>
                        setExpandedClients({ ...expandedClients, [`alloc-${cp.clientId}`]: open })
                      }
                    >
                      <Card>
                        <CollapsibleTrigger asChild>
                          <CardHeader className="cursor-pointer hover:bg-muted/50">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                {expandedClients[`alloc-${cp.clientId}`] ? (
                                  <ChevronDown className="h-5 w-5 text-muted-foreground" />
                                ) : (
                                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                                )}
                                <div>
                                  <CardTitle className="text-base">Client: {cp.clientName}</CardTitle>
                                  <p className="text-sm text-muted-foreground">
                                    {cp.portfolioAccounts.length} account{cp.portfolioAccounts.length !== 1 ? "s" : ""} &bull; {allPositions.length} position{allPositions.length !== 1 ? "s" : ""}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </CardHeader>
                        </CollapsibleTrigger>
                        <CollapsibleContent asChild>
                          <CardContent className="space-y-6">
                            <div>
                              <h4 className="text-sm font-medium mb-3">Sector Allocation</h4>
                              {sectorAllocationData.length === 0 ? (
                                <div className="text-sm text-muted-foreground text-center py-4">No positions data</div>
                              ) : (
                                <div className="space-y-3">
                                  {sectorAllocationData.map((sector) => (
                                    <div key={sector.sector} className="space-y-1">
                                      <div className="flex items-center justify-between text-sm">
                                        <span>{sector.sector}</span>
                                        <div className="flex items-center gap-4">
                                          <span className="text-muted-foreground">
                                            ${(sector.amount / 1000).toFixed(0)}K
                                          </span>
                                          <span className="font-medium w-12 text-right">
                                            {sector.value.toFixed(1)}%
                                          </span>
                                        </div>
                                      </div>
                                      <Progress value={sector.value} className="h-2" />
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>

                            <div>
                              <h4 className="text-sm font-medium mb-3">Allocation by Account Type</h4>
                              {Object.keys(categoryAccounts).length === 0 ? (
                                <div className="text-sm text-muted-foreground text-center py-4">No accounts</div>
                              ) : (
                                <div className="space-y-3">
                                  {(Object.entries(categoryAccounts) as [string, PortfolioAccountResponse[]][]).map(([category, accounts]) => {
                                    const categoryBalance = accounts.reduce((sum, a) => sum + a.balance, 0);
                                    const categoryPercent = totalCategoryBalance > 0 ? (categoryBalance / totalCategoryBalance) * 100 : 0;
                                    const Icon = getCategoryIcon(category);
                                    return (
                                      <div key={category} className="space-y-1">
                                        <div className="flex items-center justify-between text-sm">
                                          <div className="flex items-center gap-2">
                                            <div className={`p-1 rounded ${getCategoryColor(category)}`}>
                                              <Icon className="h-3.5 w-3.5" />
                                            </div>
                                            <span>{category}</span>
                                          </div>
                                          <div className="flex items-center gap-4">
                                            <span className="text-muted-foreground">
                                              ${(categoryBalance / 1000).toFixed(0)}K
                                            </span>
                                            <span className="font-medium w-12 text-right">
                                              {categoryPercent.toFixed(1)}%
                                            </span>
                                          </div>
                                        </div>
                                        <Progress value={categoryPercent} className="h-2" />
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </CollapsibleContent>
                      </Card>
                    </Collapsible>
                  );
                })
              )}
            </TabsContent>

            {/* Transactions Tab */}
            <TabsContent value="transactions" className="space-y-4">
              {clientPortfolios.length === 0 ? (
                <Card>
                  <CardContent className="py-8 text-center text-muted-foreground">
                    No client data available
                  </CardContent>
                </Card>
              ) : (
                clientPortfolios.map((cp) => {
                  const transactions = clientTransactions[cp.clientId] || [];

                  return (
                    <Collapsible
                      key={cp.clientId}
                      asChild
                      open={expandedClients[`txn-${cp.clientId}`]}
                      onOpenChange={(open) =>
                        setExpandedClients({ ...expandedClients, [`txn-${cp.clientId}`]: open })
                      }
                    >
                      <Card>
                        <CollapsibleTrigger asChild>
                          <CardHeader className="cursor-pointer hover:bg-muted/50">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                {expandedClients[`txn-${cp.clientId}`] ? (
                                  <ChevronDown className="h-5 w-5 text-muted-foreground" />
                                ) : (
                                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                                )}
                                <div>
                                  <CardTitle className="text-base">Client: {cp.clientName}</CardTitle>
                                  <p className="text-sm text-muted-foreground">
                                    {transactions.length} transaction{transactions.length !== 1 ? "s" : ""}
                                  </p>
                                </div>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); setAddingTransactionClientId(cp.clientId); }}
                              >
                                <Plus className="h-4 w-4 mr-1" />
                                Add Transaction
                              </Button>
                            </div>
                          </CardHeader>
                        </CollapsibleTrigger>
                        <CollapsibleContent asChild>
                          <CardContent>
                            {isTransactionsLoading ? (
                              <div className="flex items-center justify-center py-6 text-muted-foreground">
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Loading transactions...
                              </div>
                            ) : transactions.length === 0 ? (
                              <div className="text-center py-8 text-muted-foreground">
                                No transactions recorded yet
                              </div>
                            ) : (
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Date</TableHead>
                                    <TableHead>Type</TableHead>
                                    <TableHead>Symbol</TableHead>
                                    <TableHead className="text-right">Shares</TableHead>
                                    <TableHead className="text-right">Price</TableHead>
                                    <TableHead className="text-right">Total</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {transactions.map((tx) => (
                                    <TableRow key={tx.transactionId}>
                                      <TableCell>{tx.transactionDate}</TableCell>
                                      <TableCell>
                                        <Badge
                                          variant={
                                            tx.transactionType === "BUY" || tx.transactionType === "buy"
                                              ? "default"
                                              : tx.transactionType === "SELL" || tx.transactionType === "sell"
                                              ? "destructive"
                                              : "secondary"
                                          }
                                        >
                                          {tx.transactionType}
                                        </Badge>
                                      </TableCell>
                                      <TableCell className="font-medium">{tx.securitySymbol}</TableCell>
                                      <TableCell className="text-right">{tx.quantity || "-"}</TableCell>
                                      <TableCell className="text-right">
                                        {tx.price ? `$${tx.price.toFixed(2)}` : "-"}
                                      </TableCell>
                                      <TableCell className="text-right font-medium">
                                        ${tx.amount.toLocaleString()}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            )}
                          </CardContent>
                        </CollapsibleContent>
                      </Card>
                    </Collapsible>
                  );
                })
              )}
            </TabsContent>

            {/* Statements Tab */}
            <TabsContent value="statements" className="space-y-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Account Statements</CardTitle>
                    <Button variant="outline" size="sm" onClick={() => setIsAddingStatement(true)}>
                      <Plus className="mr-1 h-4 w-4" />
                      Add Statement
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-end gap-4">
                    <div className="space-y-2">
                      <Label>From</Label>
                      <Input
                        type="date"
                        value={advisorStatementFilters.startDate}
                        onChange={(e) =>
                          setAdvisorStatementFilters({ ...advisorStatementFilters, startDate: e.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>To</Label>
                      <Input
                        type="date"
                        value={advisorStatementFilters.endDate}
                        onChange={(e) =>
                          setAdvisorStatementFilters({ ...advisorStatementFilters, endDate: e.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Period</Label>
                      <Select
                        value={advisorStatementFilters.periodType}
                        onValueChange={(value) =>
                          setAdvisorStatementFilters({ ...advisorStatementFilters, periodType: value })
                        }
                      >
                        <SelectTrigger className="w-[140px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="DAILY">Daily</SelectItem>
                          <SelectItem value="MONTHLY">Monthly</SelectItem>
                          <SelectItem value="YEARLY">Yearly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button onClick={() => fetchClientAccountStatements()}>
                      Search
                    </Button>
                  </div>

                  {isAdvisorStatementListLoading ? (
                    <div className="flex items-center justify-center py-6 text-muted-foreground">
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Loading statements...
                    </div>
                  ) : advisorStatementListError ? (
                    <div className="text-sm text-red-600">{advisorStatementListError}</div>
                  ) : advisorStatementList.length === 0 ? (
                    <div className="text-center py-6 text-muted-foreground text-sm">
                      No statements found
                    </div>
                  ) : (
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-12 px-2"></TableHead>
                            <TableHead className="text-left">Client</TableHead>
                            <TableHead className="text-left">Custodian Account #</TableHead>
                            <TableHead className="text-left">Custodian</TableHead>
                            <TableHead>Period</TableHead>
                            <TableHead className="text-right">Start Value</TableHead>
                            <TableHead className="text-right">End Value</TableHead>
                            <TableHead className="text-right">Deposits</TableHead>
                            <TableHead className="text-right">Withdrawals</TableHead>
                            <TableHead className="text-right">Return</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {buildClientStatementRows(
                            advisorStatementList,
                            clientPortfolios,
                            expandedClients,
                            setExpandedClients,
                            setEditingAdvisorStatement,
                            setDeletingAdvisorStatementId
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Performance Overview Tab */}
            <TabsContent value="performance" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Performance by Risk Profile</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Risk Category</TableHead>
                          <TableHead className="text-right">Client Count</TableHead>
                          <TableHead className="text-right">Total AUM</TableHead>
                          <TableHead className="text-right">Avg Return</TableHead>
                          <TableHead className="text-right">% of Total AUM</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {aggregateMetrics.map((metric) => {
                          const percentage = (metric.totalValue / totalAUM) * 100;
                          return (
                            <TableRow key={metric.category}>
                              <TableCell>
                                <Badge
                                  variant={
                                    metric.category === "Conservative"
                                      ? "secondary"
                                      : metric.category === "Aggressive"
                                      ? "destructive"
                                      : "default"
                                  }
                                >
                                  {metric.category}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right">{metric.count}</TableCell>
                              <TableCell className="text-right">
                                ${(metric.totalValue / 1000000).toFixed(2)}M
                              </TableCell>
                              <TableCell className={`text-right ${returnClassName(metric.avgReturn)}`}>
                                {formatReturn(metric.avgReturn)}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center gap-2">
                                  <Progress value={percentage} className="flex-1" />
                                  <span className="text-sm text-muted-foreground w-12">
                                    {percentage.toFixed(0)}%
                                  </span>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Top Performing Accounts</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {clientPortfolios
                        .map((cp, idx) => ({
                          id: cp.clientId,
                          name: cp.clientName || cp.clientId,
                          portfolioValue: cp.portfolioAccounts.reduce((s, a) => s + (a.balance || 0), 0),
                          totalReturn: clientReturns[idx] || 0,
                        }))
                        .sort((a, b) => b.totalReturn - a.totalReturn)
                        .slice(0, 5)
                        .map((client) => (
                          <div key={client.id} className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Avatar>
                                <AvatarFallback>
                                  {client.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <div className="font-medium">{client.name}</div>
                                <div className="text-xs text-muted-foreground">
                                  ${client.portfolioValue.toLocaleString()}
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className={returnClassName(client.totalReturn)}>
                                {formatReturn(client.totalReturn)}
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </CardContent>
                </Card>

              </div>
            </TabsContent>
          </Tabs>
        )}
      </div>

      <AddAccount
        open={!!addingAccountClientId}
        onClose={() => setAddingAccountClientId(null)}
        userId={addingAccountClientId || undefined}
        onAccountsAdd={(newAccounts) => {
          setClientPortfolios((prev) => {
            if (!addingAccountClientId) return prev;
            return prev.map((cp) => {
              if (cp.clientId === addingAccountClientId) {
                return {
                  ...cp,
                  portfolioAccounts: [...cp.portfolioAccounts, ...newAccounts.map((a) => ({
                    id: a.id,
                    accountNumber: a.accountNumber,
                    custodian: (a as any).custodian || "",
                    accountNumberCustodian: a.accountNumberCustodian || "",
                    accountType: a.accountType,
                    ownershipType: a.ownershipType,
                    accountName: a.accountName,
                    balance: a.balance,
                    cashBalance: a.cashBalance,
                    ytdReturn: a.ytdReturn,
                    contributions: a.contributions || 0,
                    status: a.status,
                    openedDate: a.openedDate || null,
                    positions: (a.positions || []).map((p) => ({
                      id: p.id,
                      symbol: p.symbol,
                      name: p.name,
                      assetType: "STOCK",
                      shares: p.shares,
                      avgCost: p.avgCost,
                      currentPrice: p.price,
                      sector: p.sector,
                      marketValue: p.value,
                      change: p.change,
                      totalReturn: p.totalReturn,
                      gainLoss: null,
                      gainLossPercent: null,
                    })),
                  }))]
                };
              }
              return cp;
            });
          });
          setAddingAccountClientId(null);
        }}
      />

      <AddStatement
        open={isAddingStatement}
        onClose={() => setIsAddingStatement(false)}
        accounts={clientPortfolios.flatMap((cp) => cp.portfolioAccounts.map((a) => ({
          id: a.id,
          accountNumber: a.accountNumber,
          custodian: (a as any).custodian || "",
          accountNumberCustodian: a.accountNumberCustodian,
          accountType: a.accountType,
          ownershipType: a.ownershipType,
          accountName: a.accountName,
          balance: a.balance,
          cashBalance: a.cashBalance,
          ytdReturn: a.ytdReturn || 0,
          contributions: a.contributions || 0,
          status: a.status as "Active" | "Pending" | "Closed",
          openedDate: a.openedDate || "",
          positions: (a.positions || []).map((p) => ({
            id: p.id,
            symbol: p.symbol,
            name: p.name,
            shares: p.shares,
            price: p.currentPrice,
            avgCost: p.avgCost,
            value: p.marketValue,
            change: p.change,
            totalReturn: p.totalReturn,
            sector: p.sector || "",
          })),
        })))}
        userId={userId}
      />

      <ScheduleMeetingDialog
        professional={(() => {
          const cp = clientPortfolios.find(c => c.clientId === selectedClientId);
          return cp ? { id: cp.clientId, name: cp.clientName || cp.clientId } : null;
        })()}
        isOpen={isScheduleMeetingOpen}
        onOpenChange={setIsScheduleMeetingOpen}
        onScheduleSuccess={fetchMeetings}
      />

      <InviteOthers
        open={isInviteOpen}
        onClose={() => setIsInviteOpen(false)}
        sender={localStorage.getItem('userEmail') || ""}
      />

      {/* Add Transaction Dialog */}
      <Dialog open={!!addingTransactionClientId} onOpenChange={(open) => { if (!open) { setAddingTransactionClientId(null); setTransactionForm({ transactionDate: "", accountNumber: "", transactionType: "BUY", action: "", symbol: "", payment: "Cash", currency: "USD", quantity: "", price: "", commission: "", fees: "", interest: "", amount: "" }); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Transaction</DialogTitle>
            <DialogDescription>Record a transaction for a client account</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Transaction Date</Label>
                <Input type="date" value={transactionForm.transactionDate} onChange={(e) => setTransactionForm({ ...transactionForm, transactionDate: e.target.value })} />
              </div>
              <div className="grid gap-2">
                <Label>Account Number</Label>
                <Select value={transactionForm.accountNumber} onValueChange={(v) => setTransactionForm({ ...transactionForm, accountNumber: v })}>
                  <SelectTrigger><SelectValue placeholder="Select account" /></SelectTrigger>
                  <SelectContent>
                    {(clientPortfolios.find(cp => cp.clientId === addingTransactionClientId)?.portfolioAccounts || []).map((a) => (
                      <SelectItem key={a.id} value={a.id}>{a.accountNumberCustodian || a.accountNumber || a.accountName} - {a.accountName}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Transaction Type</Label>
                <Select value={transactionForm.transactionType} onValueChange={(v) => setTransactionForm({ ...transactionForm, transactionType: v })}>
                  <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BUY">BUY</SelectItem>
                    <SelectItem value="SELL">SELL</SelectItem>
                    <SelectItem value="REINVEST">REINVEST</SelectItem>
                    <SelectItem value="DIVIDEND">DIVIDEND</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Action</Label>
                <Input placeholder="e.g., YOU BOUGHT..." value={transactionForm.action} onChange={(e) => setTransactionForm({ ...transactionForm, action: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Symbol</Label>
                <Input placeholder="e.g., AAPL" value={transactionForm.symbol} onChange={(e) => setTransactionForm({ ...transactionForm, symbol: e.target.value })} />
              </div>
              <div className="grid gap-2">
                <Label>Payment</Label>
                <Input placeholder="Cash" value={transactionForm.payment} onChange={(e) => setTransactionForm({ ...transactionForm, payment: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Currency</Label>
                <Input placeholder="USD" value={transactionForm.currency} onChange={(e) => setTransactionForm({ ...transactionForm, currency: e.target.value })} />
              </div>
              <div className="grid gap-2">
                <Label>Amount</Label>
                <Input type="number" step="0.01" placeholder="0.00" value={transactionForm.amount} onChange={(e) => setTransactionForm({ ...transactionForm, amount: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Price</Label>
                <Input type="number" step="0.01" placeholder="0.00" value={transactionForm.price} onChange={(e) => setTransactionForm({ ...transactionForm, price: e.target.value })} />
              </div>
              <div className="grid gap-2">
                <Label>Quantity</Label>
                <Input type="number" step="0.001" placeholder="0" value={transactionForm.quantity} onChange={(e) => setTransactionForm({ ...transactionForm, quantity: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Commission</Label>
                <Input type="number" step="0.01" placeholder="0.00" value={transactionForm.commission} onChange={(e) => setTransactionForm({ ...transactionForm, commission: e.target.value })} />
              </div>
              <div className="grid gap-2">
                <Label>Fees</Label>
                <Input type="number" step="0.01" placeholder="0.00" value={transactionForm.fees} onChange={(e) => setTransactionForm({ ...transactionForm, fees: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Accrued Interest</Label>
                <Input type="number" step="0.01" placeholder="0.00" value={transactionForm.interest} onChange={(e) => setTransactionForm({ ...transactionForm, interest: e.target.value })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAddingTransactionClientId(null); setTransactionForm({ transactionDate: "", accountNumber: "", transactionType: "BUY", action: "", symbol: "", payment: "Cash", currency: "USD", quantity: "", price: "", commission: "", fees: "", interest: "", amount: "" }); }}>Cancel</Button>
            <Button onClick={handleAddTransaction}>Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Position Dialog */}
      <Dialog open={!!editingPosition} onOpenChange={(open) => { if (!open) { setEditingPosition(null); setEditingPositionAccountId(""); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Position</DialogTitle>
            <DialogDescription>Update position details</DialogDescription>
          </DialogHeader>
          {editingPosition && (
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Symbol</Label>
                  <Input
                    value={editingPosition.symbol}
                    disabled
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Name</Label>
                  <Input
                    value={editingPosition.name}
                    disabled
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Shares</Label>
                  <Input
                    type="number"
                    value={editingPosition.shares}
                    onChange={(e) => setEditingPosition({ ...editingPosition, shares: Number(e.target.value) })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Avg Cost</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingPosition.avgCost}
                    onChange={(e) => setEditingPosition({ ...editingPosition, avgCost: Number(e.target.value) })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Current Price</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingPosition.currentPrice}
                    disabled
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Sector</Label>
                  <Input
                    value={editingPosition.sector}
                    disabled
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditingPosition(null); setEditingPositionAccountId(""); }}>Cancel</Button>
            <Button onClick={handleSaveEditedPosition}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Position Confirmation */}
      <Dialog open={!!deletingPosition} onOpenChange={(open) => { if (!open) { setDeletingPosition(null); setDeletingPositionAccountId(""); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Position</DialogTitle>
            <DialogDescription>Are you sure you want to delete {deletingPosition?.symbol}?</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeletingPosition(null); setDeletingPositionAccountId(""); }}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeletePosition}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Advisor Statement Dialog */}
      <Dialog
        open={!!editingAdvisorStatement}
        onOpenChange={(open) => { if (!open) setEditingAdvisorStatement(null); }}
      >
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Account Statement</DialogTitle>
            <DialogDescription>Update account statement details</DialogDescription>
          </DialogHeader>
          {editingAdvisorStatement && (
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label>Statement Date</Label>
                <Input type="date" value={editingAdvisorStatement.statementDate}
                  onChange={(e) => setEditingAdvisorStatement({ ...editingAdvisorStatement, statementDate: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input type="date" value={editingAdvisorStatement.startDate}
                  onChange={(e) => setEditingAdvisorStatement({ ...editingAdvisorStatement, startDate: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input type="date" value={editingAdvisorStatement.endDate}
                  onChange={(e) => setEditingAdvisorStatement({ ...editingAdvisorStatement, endDate: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Period Type</Label>
                <Select value={editingAdvisorStatement.periodType}
                  onValueChange={(value) => setEditingAdvisorStatement({ ...editingAdvisorStatement, periodType: value })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DAILY">Daily</SelectItem>
                    <SelectItem value="MONTHLY">Monthly</SelectItem>
                    <SelectItem value="YEARLY">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Starting Value</Label>
                <Input type="number" value={editingAdvisorStatement.startingValue}
                  onChange={(e) => setEditingAdvisorStatement({ ...editingAdvisorStatement, startingValue: parseFloat(e.target.value) || 0 })} />
              </div>
              <div className="space-y-2">
                <Label>Ending Value</Label>
                <Input type="number" value={editingAdvisorStatement.endingValue}
                  onChange={(e) => setEditingAdvisorStatement({ ...editingAdvisorStatement, endingValue: parseFloat(e.target.value) || 0 })} />
              </div>
              <div className="space-y-2">
                <Label>Deposits</Label>
                <Input type="number" value={editingAdvisorStatement.deposits}
                  onChange={(e) => setEditingAdvisorStatement({ ...editingAdvisorStatement, deposits: parseFloat(e.target.value) || 0 })} />
              </div>
              <div className="space-y-2">
                <Label>Withdrawals</Label>
                <Input type="number" value={editingAdvisorStatement.withdrawals}
                  onChange={(e) => setEditingAdvisorStatement({ ...editingAdvisorStatement, withdrawals: parseFloat(e.target.value) || 0 })} />
              </div>
              <div className="space-y-2">
                <Label>Fees</Label>
                <Input type="number" value={editingAdvisorStatement.fees}
                  onChange={(e) => setEditingAdvisorStatement({ ...editingAdvisorStatement, fees: parseFloat(e.target.value) || 0 })} />
              </div>
              <div className="space-y-2">
                <Label>Dividends</Label>
                <Input type="number" value={editingAdvisorStatement.dividendsEarned}
                  onChange={(e) => setEditingAdvisorStatement({ ...editingAdvisorStatement, dividendsEarned: parseFloat(e.target.value) || 0 })} />
              </div>
              <div className="space-y-2">
                <Label>Interest</Label>
                <Input type="number" value={editingAdvisorStatement.interestEarned}
                  onChange={(e) => setEditingAdvisorStatement({ ...editingAdvisorStatement, interestEarned: parseFloat(e.target.value) || 0 })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingAdvisorStatement(null)}>Cancel</Button>
            <Button onClick={handleSaveEditedAdvisorStatement}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Advisor Statement Confirmation */}
      <Dialog
        open={!!deletingAdvisorStatementId}
        onOpenChange={(open) => { if (!open) setDeletingAdvisorStatementId(null); }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Statement</DialogTitle>
            <DialogDescription>Are you sure you want to delete this statement?</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingAdvisorStatementId(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => deletingAdvisorStatementId && handleDeleteAdvisorStatement(deletingAdvisorStatementId)}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Account Dialog */}
      <Dialog open={!!editingAccount} onOpenChange={(open) => { if (!open) { setEditingAccount(null); setNewPosition({ symbol: "", name: "", shares: "", avgCost: "", sector: "" }); setDeletedPositionIndices([]); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Account</DialogTitle>
            <DialogDescription>Update account details and positions</DialogDescription>
          </DialogHeader>
          {editingAccount && (
            <div className="space-y-6 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Account Name</Label>
                  <Input
                    value={editingAccount.accountName}
                    onChange={(e) => setEditingAccount({ ...editingAccount, accountName: e.target.value })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Account Number</Label>
                  <Input
                    value={editingAccount.accountNumber}
                    onChange={(e) => setEditingAccount({ ...editingAccount, accountNumber: e.target.value })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Custodian</Label>
                  <Input
                    value={(editingAccount as any).custodian || ""}
                    onChange={(e) => setEditingAccount({ ...editingAccount, custodian: e.target.value })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Custodian Account #</Label>
                  <Input
                    value={editingAccount.accountNumberCustodian}
                    onChange={(e) => setEditingAccount({ ...editingAccount, accountNumberCustodian: e.target.value })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Account Type</Label>
                  <Select
                    value={editingAccount.accountType}
                    onValueChange={(value) => setEditingAccount({ ...editingAccount, accountType: value as keyof typeof ACCOUNT_TYPES })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(ACCOUNT_TYPES).map(([key, val]) => (
                        <SelectItem key={key} value={key}>{val.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label>Ownership Type</Label>
                  <Select
                    value={editingAccount.ownershipType}
                    onValueChange={(value) => setEditingAccount({ ...editingAccount, ownershipType: value as keyof typeof OWNERSHIP_TYPES })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(OWNERSHIP_TYPES).map(([key, label]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label>Balance</Label>
                  <Input
                    type="number"
                    value={editingAccount.balance}
                    onChange={(e) => setEditingAccount({ ...editingAccount, balance: Number(e.target.value) })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Cash Balance</Label>
                  <Input
                    type="number"
                    value={editingAccount.cashBalance}
                    onChange={(e) => setEditingAccount({ ...editingAccount, cashBalance: Number(e.target.value) })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>YTD Return (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={editingAccount.ytdReturn ?? 0}
                    onChange={(e) => setEditingAccount({ ...editingAccount, ytdReturn: Number(e.target.value) })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Status</Label>
                  <Select
                    value={editingAccount.status}
                    onValueChange={(value) => setEditingAccount({ ...editingAccount, status: value as "Active" | "Pending" | "Closed" })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="Pending">Pending</SelectItem>
                      <SelectItem value="Closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Positions */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium">Positions</h3>

                {/* Add Position Form */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label>Symbol *</Label>
                    <Input
                      placeholder="AAPL"
                      value={newPosition.symbol}
                      onChange={(e) => setNewPosition({ ...newPosition, symbol: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Company Name</Label>
                    <Input
                      placeholder="Apple Inc"
                      value={newPosition.name}
                      onChange={(e) => setNewPosition({ ...newPosition, name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Sector</Label>
                    <Input
                      placeholder="Technology"
                      value={newPosition.sector}
                      onChange={(e) => setNewPosition({ ...newPosition, sector: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Shares *</Label>
                    <Input
                      type="number"
                      placeholder="100"
                      value={newPosition.shares}
                      onChange={(e) => setNewPosition({ ...newPosition, shares: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Avg Cost (per share) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="150.00"
                      value={newPosition.avgCost}
                      onChange={(e) => setNewPosition({ ...newPosition, avgCost: e.target.value })}
                    />
                  </div>
                  <div className="flex items-end">
                    <Button
                      onClick={handleAddPositionToEdit}
                      disabled={!newPosition.symbol || !newPosition.shares || !newPosition.avgCost}
                      className="w-full"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Position
                    </Button>
                  </div>
                </div>

                {/* Positions List */}
                {(editingAccount.positions || []).filter((p) => !(p as any).delete).length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-muted-foreground">
                      Positions ({(editingAccount.positions || []).filter((p) => !(p as any).delete).length})
                    </h4>
                    <div className="border rounded-lg overflow-hidden">
                      <div className="max-h-48 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-muted sticky top-0">
                            <tr>
                              <th className="text-left p-2">Symbol</th>
                              <th className="text-left p-2">Name</th>
                              <th className="text-left p-2">Sector</th>
                              <th className="text-right p-2">Shares</th>
                              <th className="text-right p-2">Avg Cost</th>
                              <th className="text-right p-2">Total</th>
                              <th className="w-10"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {(editingAccount.positions || []).map((pos, idx) =>
                              (pos as any).delete ? null : (
                                <tr key={idx} className="border-t">
                                  <td className="p-2">{pos.symbol}</td>
                                  <td className="p-2 text-muted-foreground">{pos.name}</td>
                                  <td className="p-2">
                                    <Badge variant="outline" className="text-xs">{pos.sector}</Badge>
                                  </td>
                                  <td className="p-2 text-right">{pos.shares}</td>
                                  <td className="p-2 text-right">${pos.avgCost.toFixed(2)}</td>
                                  <td className="p-2 text-right">${(pos.shares * pos.avgCost).toFixed(2)}</td>
                                  <td className="p-2">
                                    <button
                                      onClick={() => handleRemovePositionFromEdit(idx)}
                                      className="text-muted-foreground hover:text-destructive"
                                    >
                                      <X className="h-4 w-4" />
                                    </button>
                                  </td>
                                </tr>
                              )
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditingAccount(null); setNewPosition({ symbol: "", name: "", shares: "", avgCost: "", sector: "" }); setDeletedPositionIndices([]); }}>Cancel</Button>
            <Button onClick={() => editingAccount && handleEditAccount(editingAccount)} disabled={isSavingAccount}>
              {isSavingAccount && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
